import { type FormInstance } from 'antd';

import { Schemas } from '@api-client/generated/types';

/**
 * Billing settings as this portal reads them.
 *
 * `Schemas.BillingSettingsDto` only gains the CCSS/ACD intermediation pair once the API
 * client is regenerated against a stage backend carrying ENG-1316, so the two fields are
 * declared here to keep the portal compiling either way. They are optional in both
 * directions: companies stored before the feature carry neither key. Drop the
 * intersection once the regenerated client has them.
 */
export type BillingSettings = NonNullable<
  Schemas.AdminCompanyResponseDto['billingSettings']
> & {
  isCcssAcdIntermediationEnabled?: boolean;
  ccssAcdIntermediationPrice?: number;
};

/**
 * A company's billing settings, widened to the shape above. No cast: the generated DTO
 * is already assignable, since everything the intersection adds is optional.
 */
export const readBillingSettings = (
  company: Schemas.AdminCompanyResponseDto
): BillingSettings | undefined => company.billingSettings;

/** Default monthly fee for Intermediation with CCSS and ACD, in euro before VAT. */
export const DEFAULT_CCSS_ACD_INTERMEDIATION_PRICE = 51;

/**
 * `isBillingEnabled` as a boolean, wherever it is read from.
 *
 * The two sources spell it differently: the stored `billing_settings` blob carries a real
 * boolean (the backend's DTO coerces on the way in), while anything that came through the
 * "Billing enabled" select is the string 'true' or 'false' — and so is the form in *every*
 * state, because the load effect writes a `String()` of the stored value rather than the value.
 * The boolean arm is therefore for the blob only, not for the form.
 */
export const toBillingEnabled = (value: unknown): boolean =>
  value === true || value === 'true';

/**
 * Converts a form field value to a number for validation.
 * Returns NaN for empty/unfilled values to ensure they fail validation.
 */
const toNumberOrNaN = (value: unknown): number =>
  value === undefined || value === null || value === '' ? NaN : Number(value);

/**
 * Billing settings as the edit form holds them right now, with every price coerced to a
 * number so a blank input fails validation instead of reading as 0.
 *
 * Shared with the form's field-level validators so they all judge the same snapshot.
 */
export const readBillingSettingsFromForm = (
  form: FormInstance
): BillingSettings => {
  const billingSettings = form.getFieldValue('billingSettings');

  return {
    ...billingSettings,
    isBillingEnabled: toBillingEnabled(billingSettings?.isBillingEnabled),
    baseServicePrice: toNumberOrNaN(billingSettings?.baseServicePrice),
    documentsIncluded: toNumberOrNaN(billingSettings?.documentsIncluded),
    payslipPrice: toNumberOrNaN(billingSettings?.payslipPrice),
    payslipAmount: toNumberOrNaN(billingSettings?.payslipAmount),
    ccssAcdIntermediationPrice: toNumberOrNaN(
      billingSettings?.ccssAcdIntermediationPrice
    ),
  };
};

export interface BillingSettingsValidationResult {
  isValid: boolean;
  errors: {
    baseServicePrice?: string;
    documentsIncluded?: string;
    payslipPrice?: string;
    payslipAmount?: string;
    ccssAcdIntermediationPrice?: string;
  };
}

/**
 * Helper to check if a value is a valid finite number.
 * Returns false for undefined, null, NaN, Infinity, and non-number types.
 *
 * @param value - The value to check
 * @returns True if the value is a finite number, false otherwise
 */
const isValidNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Validates the billing settings fields when billing is enabled.
 * This is a reusable function that can be used for form validation.
 *
 * Validation rules:
 * - Base service price must be greater than 0
 * - Documents included must be 0 or greater
 * - Payslip amount must be 0 or greater (cannot be negative)
 * - Payslip amount and payslip price are interdependent: both must be set (>0)
 *   together or both must be unset/zero. If only one is provided, both fields
 *   are marked as invalid with symmetric error messages.
 * - The CCSS/ACD intermediation price is required, and must be greater than 0,
 *   only while its toggle is on.
 *
 * @param billingSettings - The billing settings object from the company DTO
 * @returns Validation result containing isValid flag and any field-specific errors
 */
export const validateBillingSettingsFields = (
  billingSettings: BillingSettings | undefined
): BillingSettingsValidationResult => {
  const errors: BillingSettingsValidationResult['errors'] = {};

  const baseServicePrice = billingSettings?.baseServicePrice;
  const documentsIncluded = billingSettings?.documentsIncluded;
  const payslipPrice = billingSettings?.payslipPrice;
  const payslipAmount = billingSettings?.payslipAmount;
  const ccssAcdIntermediationPrice =
    billingSettings?.ccssAcdIntermediationPrice;

  // Base service price must be a positive number (required field)
  if (!isValidNumber(baseServicePrice) || baseServicePrice <= 0) {
    errors.baseServicePrice = 'Base service price must be greater than 0';
  }

  // Documents included: if provided, must be a valid non-negative number
  // (empty/unset is allowed - optional field)
  if (isValidNumber(documentsIncluded) && documentsIncluded < 0) {
    errors.documentsIncluded = 'Documents included must be 0 or greater';
  }

  // Payslip price: if provided, must be a valid non-negative number
  // (empty/unset is allowed - means payroll not configured)
  if (isValidNumber(payslipPrice) && payslipPrice < 0) {
    errors.payslipPrice = 'Payslip price must be 0 or greater';
  }

  // Payslip amount: if provided, must be a valid non-negative number
  // (empty/unset is allowed - means payroll not configured)
  if (isValidNumber(payslipAmount) && payslipAmount < 0) {
    errors.payslipAmount = 'Payslip amount must be 0 or greater';
  }

  // Cross-validation: payslip amount and price must both be valid or both be unset/zero
  const hasValidPayslipAmount =
    isValidNumber(payslipAmount) && payslipAmount > 0;
  const hasValidPayslipPrice = isValidNumber(payslipPrice) && payslipPrice > 0;

  if (hasValidPayslipAmount !== hasValidPayslipPrice) {
    errors.payslipAmount = hasValidPayslipAmount
      ? 'Payslip amount requires payslip price to also be set'
      : 'Payslip amount must be greater than 0 when payslip price is set';
    errors.payslipPrice = hasValidPayslipPrice
      ? 'Payslip price requires payslip amount to also be set'
      : 'Payslip price must be greater than 0 when payslip amount is set';
  }

  // Intermediation price: only meaningful while the toggle is on. The backend skips
  // the whole line when the price is missing, so a blank input would silently drop the
  // charge from every future draft rather than fail loudly here.
  if (
    billingSettings?.isCcssAcdIntermediationEnabled &&
    (!isValidNumber(ccssAcdIntermediationPrice) ||
      ccssAcdIntermediationPrice <= 0)
  ) {
    errors.ccssAcdIntermediationPrice =
      'Intermediation price must be greater than 0';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};
